import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logoUrl from '../assets/logo.png';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import {
  LayoutDashboard,
  BookOpen,
  Users,
  GraduationCap as StudentIcon,
  FileCheck,
  Award,
  Bell,
  Image,
  HelpCircle,
  MapPin,
  Trophy,
  MessageSquare,
  Star,
  Ticket,
  LogOut,
  Menu,
  GraduationCap,
  ChevronRight,
  Home,
  Settings,
  UserCheck
} from 'lucide-react';

const adminNavItems = [
  { name: 'Dashboard', path: '/admin', icon: LayoutDashboard },
  { name: 'Courses', path: '/admin/courses', icon: BookOpen },
  { name: 'Faculty', path: '/admin/faculty', icon: Users },
  { name: 'Students', path: '/admin/students', icon: StudentIcon },
  { name: 'Enrollments', path: '/admin/enrollments', icon: FileCheck },
  { name: 'Scholarships', path: '/admin/scholarships', icon: Award },
  { name: 'Notices', path: '/admin/notices', icon: Bell },
  { name: 'Gallery', path: '/admin/gallery', icon: Image },
  { name: 'FAQs', path: '/admin/faqs', icon: HelpCircle },
  { name: 'Centers', path: '/admin/centers', icon: MapPin },
  { name: 'Toppers', path: '/admin/toppers', icon: Trophy },
  { name: 'Testimonials', path: '/admin/testimonials', icon: Star },
  { name: 'Directors', path: '/admin/directors', icon: UserCheck },
  { name: 'Inquiries', path: '/admin/inquiries', icon: MessageSquare },
  { name: 'Support Tickets', path: '/admin/tickets', icon: Ticket },
];

const SidebarContent = ({ onItemClick }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-6 border-b">
        <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
          <div className="w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center">
            <img src={logoUrl} alt="Caliber Nova" className="w-full h-full object-contain" />
          </div>
          <div className="hidden sm:block">
            <span className="font-heading font-bold text-xl text-primary">Caliber Nova</span>
            <span className="block text-xs text-muted-foreground -mt-1">Admin Panel</span>
          </div>
        </Link>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 px-4 py-6">
        <nav className="space-y-1">
          {adminNavItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={onItemClick}
              data-testid={`admin-nav-${item.name.toLowerCase().replace(' ', '-')}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${location.pathname === item.path
                ? 'bg-primary text-white shadow-md'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
            >
              <item.icon className="w-5 h-5" />
              {item.name}
              {location.pathname === item.path && (
                <ChevronRight className="w-4 h-4 ml-auto" />
              )}
            </Link>
          ))}
        </nav>
      </ScrollArea>

      {/* User Info & Logout */}
      <div className="p-4 border-t">
        <div className="flex items-center gap-3 mb-4 p-3 bg-muted rounded-lg">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Settings className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground">Administrator</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" asChild className="flex-1" data-testid="admin-home-btn">
            <Link to="/">
              <Home className="w-4 h-4 mr-2" />
              Site
            </Link>
          </Button>
          <Button variant="outline" size="sm" onClick={handleLogout} className="flex-1 text-destructive hover:text-destructive" data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>
    </div>
  );
};

export const AdminLayout = ({ children, title }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isAuthenticated, isAdmin, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading) {
      if (!isAuthenticated) {
        navigate('/login');
      } else if (!isAdmin) {
        navigate('/dashboard');
      }
    }
  }, [isAuthenticated, isAdmin, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-50 lg:block lg:w-64 lg:bg-white lg:border-r">
        <SidebarContent />
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-40 flex h-14 items-center gap-4 border-b bg-white px-4">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="admin-mobile-trigger">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-64">
            <SidebarContent onItemClick={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>
        <h1 className="font-heading font-semibold text-lg">{title}</h1>
      </header>

      {/* Main Content */}
      <main className="lg:pl-64">
        <div className="p-4 md:p-6 lg:p-8">
          {/* Page Title - Desktop */}
          {title && (
            <div className="hidden lg:block mb-6">
              <h1 className="font-heading text-2xl font-bold text-foreground">{title}</h1>
            </div>
          )}
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
